// A fixed-width worker pool over a list, reporting progress as it goes. The
// width is a decision made here rather than `Promise.all` over whatever the
// operator selected: these calls spend per-token quotas (Mapbox, Companies
// House, HMLR, OSM) shared with every operator and the scheduled jobs.
export const REFETCH_CONCURRENCY = 4;

// Select-all is one click on every board, so above this width say how big the
// batch is before spending a quota call per row. Shared so both boards ask at
// the same size.
const FORCE_REFRESH_CONFIRM_ABOVE = 25;

export const confirmLargeForceRefresh = (count: number): boolean =>
  count <= FORCE_REFRESH_CONFIRM_ABOVE ||
  window.confirm(
    `Force refresh ${count} rows? Each one is re-fetched from Mapbox, ` +
      `Companies House, HMLR and OSM, against quotas shared with everyone else.`
  );

// `run` must handle its own failures - a throw here aborts the whole batch.
export const runBounded = async <T>(
  items: T[],
  run: (item: T) => Promise<void>,
  onProgress: (done: number) => void,
  concurrency: number = REFETCH_CONCURRENCY
): Promise<void> => {
  let nextIdx = 0;
  let finished = 0;
  const worker = async (): Promise<void> => {
    while (nextIdx < items.length) {
      const i = nextIdx++;
      if (i >= items.length) return;
      await run(items[i]);
      finished += 1;
      onProgress(finished);
    }
  };
  await Promise.all(
    Array.from({ length: Math.min(concurrency, items.length) }, worker)
  );
};
